// =============================================================================
// SECTION-HEADER ACCENT TYPEWRITER — Horizon `Component` port (Wave 2) of the bundled
// src/js/objects/global/section-header-typing.js. `<section-header-accent>` IS the accent element
// itself (snippets/section-header.liquid swaps the old `<span class="section-header__accent">` to
// this tag, keeping class + data-animate-accent + inline style verbatim), loaded as native ESM via
// the `@theme/*` importmap (snippets/horizon-importmap.liquid) rather than the webpack src/js bundle.
//
// The effect (styled in src/scss/objects/global/_section-header.scss): on first scroll-into-view
// (≥50% visible, once per element ever) the slanted Caveat slogan's text is split into
// per-character spans, hidden via the `--pending` OPACITY state, then revealed one-by-one on a
// timer with a blinking caret tracking the typing front.
//
// SCOPE NOTE (2026-07-05): this change-set is NOT a purely zero-visual-delta driver swap — it folds
// in the caret-geometry fix: the old module (src/js/objects/global/section-header-typing.js, the
// caret block ~74-99) and the _section-header.scss caret rules were rewritten in lockstep (inline
// zero-advance caret → absolutely positioned overlay), a deliberate VISUAL fix that kills a ~1px
// inline residual and a ~10px end-of-animation line-box drop. This Component mirrors the POST-fix
// code byte-for-byte, so old↔new driver parity still holds.
//
// PARITY NOTES (behaviour identical to the old bundled module — do not "improve"):
//   • Guard ORDER in connect matches the old bindAccent() exactly: bound-flag → data-animate-accent
//     !== 'true' (return WITHOUT flag) → empty text (return WITHOUT flag) → SET flag →
//     reduced-motion / no-IntersectionObserver (return AFTER flag = permanently static full slogan)
//     → observe. Reduced motion is evaluated ONCE at bind time (point-in-time semantic, kept); the
//     CSS safety net in _section-header.scss covers a mid-animation media change.
//   • TRANSITIONAL DOUBLE-DRIVE GUARD: this component checks AND sets the SAME dataset key as the
//     old module (`shAccentTypingBound` → data-sh-accent-typing-bound), first-binder-wins — so
//     while the old bundle (assets/global.autojs.js) still contains the module, whichever driver
//     binds an accent first owns it and the other no-ops. Both drivers behave identically, so the
//     overlap window is benign. Keep this key until the old module is fully retired.
//   • Chars are wrapped at IO-FIRE time, NOT at connect — the accent shows its plain static text
//     while <50% visible, then blanks (all chars --pending) and types. Do NOT pre-wrap at connect;
//     that would change first-paint visuals.
//   • wrapChars walks the DOM recursively so inline_richtext markup (<em>/<strong>/<a>) survives;
//     Array.from() splits by code point (surrogate-safe). All char spans REMAIN in the DOM after
//     typing (final DOM ≠ initial DOM by design); the caret is removed after a 700ms linger.
//   • Full slogan text stays in the DOM + a11y tree at ALL times (opacity-only hiding, caret is
//     aria-hidden); the accent is absolute + nowrap so its width is reserved — NO layout shift.
//   • Caret geometry: char offsetLeft/offsetTop resolve against the accent as offsetParent (the
//     accent is position:absolute per SCSS). THE HOST MUST REMAIN THE POSITIONED ACCENT NODE — no
//     intermediate positioned wrapper, or caret coords break under the -11.35deg rotation.
//   • No shopify:section:load listener: editor re-render disconnects/reconnects (fresh DOM has no
//     bound flag → rearms → types when visible), identical net effect to the old full rescan. No
//     shopify:block:select/deselect handling — the original has none.
//   • Timers are NOT cancelled on disconnect (the old module never cancels them either); a detached
//     mid-typing chain is harmless and GC'd with its nodes.
//
// CHANGES vs the old module (wiring only, no behaviour delta):
//   • The IntersectionObserver is per-instance, released in disconnectedCallback and re-armed on
//     reconnect while un-typed — fixes the old module's leaked un-fired observers (GC-reliant).
//   • Once-per-element-ever is tracked via a private field (#settled) in addition to the persistent
//     dataset flag; no new data-* attributes are added to the DOM.
//   • KNOWN LATENT DELTA (documented, accepted): a custom element auto-connects on AJAX-INJECTED
//     markup, so the two innerHTML surfaces the old module never covered (product-recommendations
//     PDP branch, recently-viewed) would now animate IF they carried a live accent. Current live
//     configs make this invisible (product-recommendations accent is '' on PDP; recently-viewed
//     preset ships show_slogan:false). The old no-animation there was an accident of init timing,
//     not design.
//
// No refs= hooks: the char spans + caret are JS-created at runtime, and data-animate-accent is
// merchant-facing config on the host, not an internal query hook.
// `type="module"` scripts DEFER + dedupe per URL, so the shared snippet may emit the script tag at
// every render site (~45 across the estate); it loads once. UNTRACKED new file (alongside
// assets/component.js + assets/utilities.js) — re-verify it exists before any push.
// =============================================================================
import { Component } from '@theme/component';

const CHAR_CLASS = 'section-header__accent-char';
const CHAR_PENDING = 'section-header__accent-char--pending';
const CHAR_TYPED = 'section-header__accent-char--typed';
const CARET_CLASS = 'section-header__accent-caret';
const BOUND_FLAG = 'shAccentTypingBound'; // dataset key → data-sh-accent-typing-bound (SHARED with the old module — double-drive guard)

const STEP_MS = 60; // delay between characters
const START_DELAY_MS = 140; // small pause before the first character (caret blinks during it)
const CARET_LINGER_MS = 700; // caret keeps blinking briefly after the last char, then is removed
const VISIBLE_THRESHOLD = 0.5; // fraction of the accent in view before typing starts

function prefersReducedMotion() {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

// Recursively wrap every character of every descendant TEXT node in a `--pending` (hidden) span,
// preserving element wrappers so inline_richtext markup survives. Returns the char spans in document
// order so the caller can reveal them sequentially. Array.from() splits by code point (surrogate-safe).
function wrapChars(node, collected) {
  Array.from(node.childNodes).forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      const chars = Array.from(child.textContent);
      if (chars.length === 0) return;
      const fragment = document.createDocumentFragment();
      chars.forEach((char) => {
        const span = document.createElement('span');
        span.className = `${CHAR_CLASS} ${CHAR_PENDING}`;
        span.textContent = char;
        collected.push(span);
        fragment.appendChild(span);
      });
      node.replaceChild(fragment, child);
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      if (child.classList && child.classList.contains(CARET_CLASS)) return; // defensive: never re-split a caret
      wrapChars(child, collected);
    }
  });
}

function typeAccent(accent) {
  const chars = [];
  wrapChars(accent, chars);
  if (chars.length === 0) return; // whitespace-only accent — nothing to reveal, leave as rendered

  // Optional blinking caret — an ABSOLUTELY POSITIONED overlay (see _section-header.scss) that sits at
  // the typing front, blinks via CSS, then is removed when done. Being out of flow it contributes zero
  // to the accent's width / line-box, so moving or removing it never shifts the slogan (no end-of-anim
  // jump). aria-hidden so it never leaks into the (already-complete) accessible slogan text.
  const caret = document.createElement('span');
  caret.className = CARET_CLASS;
  caret.setAttribute('aria-hidden', 'true');
  accent.appendChild(caret); // out of flow → DOM position is irrelevant; JS drives its coords

  // Position the caret at a glyph edge using LAYOUT offsets. offsetLeft/offsetTop are in the accent's own
  // pre-transform coordinate space (they ignore the CSS rotate), and each char's offsetParent is the
  // absolutely positioned accent — so this lands the caret correctly under the slant. `trailing` puts it
  // just after the char (the typed front); otherwise at the char's leading edge (the pre-typing start).
  const caretAt = (span, trailing) => {
    caret.style.left = `${span.offsetLeft + (trailing ? span.offsetWidth : 0)}px`;
    caret.style.top = `${span.offsetTop}px`;
  };

  caretAt(chars[0], false); // start: caret at the leading edge of the first (still-hidden) char

  let index = 0;
  const step = () => {
    const span = chars[index];
    span.classList.remove(CHAR_PENDING);
    span.classList.add(CHAR_TYPED);
    caretAt(span, true); // move caret to just after the char just typed
    index += 1;

    if (index < chars.length) {
      window.setTimeout(step, STEP_MS);
    } else {
      window.setTimeout(() => {
        if (caret.parentNode) caret.parentNode.removeChild(caret);
      }, CARET_LINGER_MS);
    }
  };

  window.setTimeout(step, START_DELAY_MS);
}

/**
 * The section-header accent element itself (class `section-header__accent` — the positioned node,
 * required for caret offset geometry). Types its own slogan on first ≥50% scroll-into-view.
 *
 * @extends {Component}
 */
export class SectionHeaderAccent extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** This instance passed the opt-in gates and set the bound flag (owns the effect). */
  #armed = false;

  /** Typing has started, or the accent is permanently static — never observe again. */
  #settled = false;

  connectedCallback() {
    super.connectedCallback(); // populates this.refs (unused here) + wires Component events

    if (this.#settled || this.#observer) return;

    if (!this.#armed) {
      // Same gates, same ORDER, as the old module's bindAccent() — order matters for rescan/
      // reconnect semantics (gates 2–3 return WITHOUT the flag so a later pass re-checks).
      if (this.dataset[BOUND_FLAG] === 'true') {
        // First-binder-wins: the old bundled module (or a prior driver) already owns this accent.
        this.#settled = true;
        return;
      }
      if (this.getAttribute('data-animate-accent') !== 'true') return;
      if (this.textContent.trim().length === 0) return;
      this.dataset[BOUND_FLAG] = 'true';
      this.#armed = true;

      // Reduced motion (or no IntersectionObserver) → show the full slogan immediately, no
      // animation — evaluated ONCE at bind time, flag stays set (permanently static; parity).
      if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
        this.#settled = true;
        return;
      }
    }

    this.#observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          obs.unobserve(entry.target); // once per element ever
          obs.disconnect();
          this.#observer = null;
          this.#settled = true;
          typeAccent(entry.target);
        });
      },
      { threshold: VISIBLE_THRESHOLD }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Wiring-only fix vs the old module (which leaked un-fired observers, GC-reliant): release the
    // observer on disconnect. Re-armed on reconnect while un-typed (#settled gates re-arming).
    this.#observer?.disconnect();
    this.#observer = null;
  }
}

if (!customElements.get('section-header-accent')) {
  customElements.define('section-header-accent', SectionHeaderAccent);
}
